import React, { useRef, useState } from 'react';
import { Modal, Button, Image, Form, Alert, Spinner } from 'react-bootstrap';
import { BiMessageAltError } from 'react-icons/bi';
import { applyLicenseKey, extractLicenseKey } from '../License/api';
import { INK, STATUS } from 'Utils/styleTokens';
import './LicenseDialog.css';

const resolveLicenseState = (licenseData) => {
  const data = licenseData || {};

  // 403 error body shape from LicenseEntitlementFilter:
  // { error: 'LICENSE_REQUIRED' | 'LICENSE_EXPIRED', feature, message }
  if (data.error === 'LICENSE_REQUIRED' || data.error === 'LICENSE_EXPIRED') {
    return {
      tenantId: null,
      expired: data.error === 'LICENSE_EXPIRED',
      feature: data.feature || null
    };
  }

  // /api/v1/platform/license status shape:
  // { tenantId, source, appliedAt, features: { key: { expiresAt, valid } } }
  const features = data.features || {};
  const anyValid = Object.values(features).some((f) => f && f.valid);
  const anyExpired = Object.values(features).some((f) => f && !f.valid && f.expiresAt);
  return {
    tenantId: data.tenantId || null,
    expired: !anyValid && anyExpired,
    feature: null
  };
};

const LicenseDialog = (props) => {
  const { tenantId, expired, feature } = resolveLicenseState(props.licenseData);
  const [licenseInput, setLicenseInput] = useState('');
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [applySuccess, setApplySuccess] = useState(false);
  const fileInputRef = useRef(null);

  const title = expired ? 'License Expired' : 'License Required';
  const message = expired
    ? 'Your C2 Privacy Platform license has expired.'
    : (feature
        ? `The "${feature}" feature is not licensed on this platform.`
        : 'A valid license is required to continue using the platform.');

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setLicenseInput(text);
      setApplyError(null);
    } catch (err) {
      setApplyError('Could not read file: ' + (err?.message || err));
    } finally {
      e.target.value = '';
    }
  };

  const onApply = async () => {
    const key = extractLicenseKey(licenseInput);
    if (!key) {
      setApplyError('Paste a license key or choose a license file first.');
      return;
    }
    setApplying(true);
    setApplyError(null);
    setApplySuccess(false);
    try {
      const resp = await applyLicenseKey(key);
      if (resp && resp.features) {
        setApplySuccess(true);
        setLicenseInput('');
        if (props.onLicenseApplied) {
          props.onLicenseApplied();
        }
      } else {
        setApplyError('Invalid license key. The server rejected it.');
      }
    } catch (err) {
      setApplyError(err?.message || 'Failed to apply license.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Modal
      className='p-5 license-dialog'
      size='lg'
      centered
      show={!!props.licenseIssue}
    >
      <Modal.Body className='text-center p-5'>
        <div className='header-container'>
          {expired
            ? <BiMessageAltError className='mt-5' size={75} color={STATUS.danger.dot} />
            : <Image className='w-100 h-100' src='/c2_logo_square.png' />}
        </div>
        <h3 className='mb-2 mt-2'>{title}</h3>
        <p className='mb-3 mt-2'>{message}</p>

        {tenantId && (
          <div className='mb-3 p-3' style={{ background: INK[50], borderRadius: '10px', display: 'inline-block' }}>
            <div style={{ fontSize: '12px', color: INK[500] }}>Send this Tenant ID to C<sup>2</sup> Data:</div>
            <code style={{ fontSize: '14px' }}>{tenantId}</code>
          </div>
        )}

        {/* Inline license update form */}
        <div className='text-left mb-4 mt-2' style={{ borderTop: `1px solid ${INK[150]}`, paddingTop: '20px' }}>
          <p className='mb-2' style={{ fontSize: '13px', fontWeight: 600 }}>Apply License Key</p>
          <Form.Group className='mb-2'>
            <Form.Control
              as='textarea'
              rows={4}
              placeholder='Paste license key or JSON here…'
              value={licenseInput}
              onChange={(e) => { setLicenseInput(e.target.value); setApplyError(null); setApplySuccess(false); }}
              style={{ fontFamily: 'monospace', fontSize: '12px', resize: 'vertical' }}
            />
          </Form.Group>
          <div className='d-flex align-items-center mb-2' style={{ gap: '8px' }}>
            <Button
              size='sm'
              variant='outline-secondary'
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              disabled={applying}
            >
              Choose File…
            </Button>
            <span style={{ fontSize: '12px', color: INK[400] }}>or paste the key above</span>
            <input
              ref={fileInputRef}
              type='file'
              accept='.json,.txt,.lic'
              style={{ display: 'none' }}
              onChange={onFileChange}
            />
          </div>
          {applyError && (
            <Alert variant='danger' className='py-2 px-3 mb-2' style={{ fontSize: '13px' }}>{applyError}</Alert>
          )}
          {applySuccess && (
            <Alert variant='success' className='py-2 px-3 mb-2' style={{ fontSize: '13px' }}>License applied successfully.</Alert>
          )}
          <Button
            variant='primary'
            onClick={onApply}
            disabled={applying || !licenseInput.trim()}
            style={{ minWidth: '120px' }}
          >
            {applying ? <Spinner animation='border' size='sm' className='mr-2' /> : null}
            {applying ? 'Saving…' : 'Save License'}
          </Button>
        </div>

        <div className='d-flex justify-content-center'>
          <Button
            size='lg'
            variant='outline-secondary'
            onClick={() => props.closeModalHandler && props.closeModalHandler()}
          >
            Dismiss
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default LicenseDialog;
